<template>
  <FilterControlGroup :class="statusClasses">
    <template v-for="option in options" :key="option.label">
      <div class="space-y-1">
        <div class="flex items-center justify-between">
          <label class="text-xs font-medium text-gray-600 dark:text-gray-400">
            {{ option.label }}
          </label>
          <button
            v-if="modelValue[option.key]?.length"
            type="button"
            class="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            @click="modelValue[option.key] = []"
          >
            {{ $t('general.clear') }}
          </button>
        </div>
        <FilterOptionSelect
          :model-value="modelValue[option.key] ?? []"
          :options="option.items ?? []"
          :placeholder="option.valueLabel?.() ?? option.label"
          @update:model-value="(value: any[]) => modelValue[option.key] = value"
        />
      </div>
    </template>

    <div class="space-y-1">
      <label class="text-xs font-medium text-gray-600 dark:text-gray-400">
        {{ $t('components.customer.list.fieldsWithDateRange.during') }}
      </label>
      <DateRangeFilter v-model="modelValue.date" />
    </div>
  </FilterControlGroup>
</template>

<script setup lang="ts">
import DateRangeFilter from './DateRangeFilter.vue'
import FilterControlGroup from './FilterControlGroup.vue'
import FilterOptionSelect from './FilterOptionSelect.vue'

interface Props {
  options: Record<string, any>
  defaultStatusClass?: string | Record<string, any> | any[]
  warningStatusClass?: string | Record<string, any> | any[]
  errorStatusClass?: string | Record<string, any> | any[]
  successStatusClass?: string | Record<string, any> | any[]
  size?: string | null
  status?: boolean | string | null
}

const props = withDefaults(defineProps<Props>(), {
  defaultStatusClass: 'bg-white',
  warningStatusClass: 'border-yellow-400 bg-yellow-100 dark:bg-yellow-400/20',
  errorStatusClass: 'border-red-300 bg-red-100 dark:bg-red-400/20',
  successStatusClass: 'border-green-300 bg-green-100 dark:bg-green-400/20',
  size: null,
  status: null,
})

const modelValue = defineModel<Record<string, any>>({ default: () => ({}) })

// Mixin logic from classes.js
const noStatus = computed(() => props.status === null)
const isSuccess = computed(() => props.status === true || props.status === 'success')
const isError = computed(() => props.status === false || props.status === 'error')
const isWarning = computed(() => props.status === 'warning')

const statusClasses = computed(() => {
  const result: any[] = []

  if (!noStatus.value && isError.value && props.errorStatusClass) {
    result.push(props.errorStatusClass)
  } else if (!noStatus.value && isSuccess.value && props.successStatusClass) {
    result.push(props.successStatusClass)
  } else if (!noStatus.value && isWarning.value && props.warningStatusClass) {
    result.push(props.warningStatusClass)
  } else if (noStatus.value && props.defaultStatusClass) {
    result.push(props.defaultStatusClass)
  }

  return result
})
</script>
